import React, { useState, useEffect } from "react";
import { z } from "zod";
import { useStoredPreference } from "@/hooks/useStoredPreference";
import { useTranslation } from "react-i18next";
import { PageHeader } from "@/components";
import { tenantApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CompaniesTable } from "@/pages/Companies/CompaniesTable";
import { NewCompanyModal } from "@/pages/Companies/NewCompanyModal";

import type { CompanyItem } from "@/pages/Companies/types";

export const Companies: React.FC = () => {
    const { t } = useTranslation("global");
    const { isAdmin } = useAuth();
    const [items, setItems] = useState<CompanyItem[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [search, setSearch] = useStoredPreference(
        "vigepra_companies_search",
        z.string().max(500),
        "",
        true
    );
    const [isNewModalOpen, setIsNewModalOpen] = useState<boolean>(false);

    const fetchItems = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await tenantApi.getAll();
            if (res.result) {
                setItems(res.data.companies || []);
            }
        } catch (failure: unknown) {
            setError(
                failure instanceof Error ? failure.message : t("common.error")
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchItems();
    }, []);

    const handleUpdateItem = (updated: CompanyItem) => {
        setItems((prev) =>
            prev.map((item) =>
                item.id === updated.id ? { ...item, ...updated } : item
            )
        );
    };

    const handleDeleteItem = (deletedId: number | string) => {
        setItems((prev) => prev.filter((item) => item.id !== deletedId));
    };

    const displayedItems = items.filter((item: CompanyItem) => {
        if (search) {
            const s = search.toLowerCase().trim();
            const name = String(
                item.data?.name || item.name || ""
            ).toLowerCase();
            const id = String(item.id ?? "").toLowerCase();
            const db = String(
                item.data?.database || item.database || ""
            ).toLowerCase();
            const vigepra1 = String(
                item.data?.vigepra1 || item.vigepra1 || ""
            ).toLowerCase();

            if (
                !name.includes(s) &&
                !id.includes(s) &&
                !db.includes(s) &&
                !vigepra1.includes(s)
            ) {
                return false;
            }
        }
        return true;
    });

    return (
        <div className="page">
            <PageHeader
                compact
                className="!mb-0"
                title={<span>{t("companies.title")}</span>}
                action={
                    isAdmin
                        ? {
                              label: t("companies.new_title"),
                              icon: "plusCircle",
                              onClick: () => setIsNewModalOpen(true),
                          }
                        : null
                }
            />

            <CompaniesTable
                items={displayedItems}
                loading={loading}
                error={error}
                search={search}
                onSearchChange={setSearch}
                onUpdateItem={handleUpdateItem}
                onDeleteItem={handleDeleteItem}
                onRefresh={fetchItems}
            />

            <NewCompanyModal
                isOpen={isNewModalOpen}
                onClose={() => setIsNewModalOpen(false)}
                onSuccess={() => fetchItems()}
            />
        </div>
    );
};
